import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { assetCached } from '../../data/assets/helpers';
import { TpAssetInfo } from '../../data/assets/interfaces';
import {
  addNewAsset,
  addOpenedAsset,
  closeOpenedAsset,
  findOpenedAsset,
  selectQueriedAssets,
  setAssetLastSearchQuery,
} from '../../data/assets/queriedAssetsSlice';
import styles from './AssetView.module.scss';
import { AssetViewFactory } from './AssetViewFactory';
import { selectTemplatesAndCombinedOntology, addTemplate } from '../../data/templateSlice';
import { SectionsIndexWrapper, getSectionsIndexContext } from '../../components/sections-index/SectionsIndex';
import { showErrorMessage } from '../../components/message-boxes/ErrorMessage';

import { useEffect, useRef, useState } from 'react';
import { useApiContext } from '../../api';

export function AssetView() {
  const dispatch = useAppDispatch();
  const api = useApiContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { assetViews, ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const queriedAssetsData = useAppSelector(selectQueriedAssets);
  const { assetId } = useParams();
  const [assetInfo, setAssetInfo] = useState<TpAssetInfo | null>(null);
  const [assetViewTemplate, setAssetViewTemplate] = useState<any | null>(null);
  const assetRequestToken = useRef(0);

  useEffect(() => {
    const requestToken = ++assetRequestToken.current;
    setAssetInfo(null);
    setAssetViewTemplate(null);
    if (!assetId) {
      setTimeout(() => {
        navigate('/');
      }, 50);
      return;
    }
    if (!assetCached(assetId, queriedAssetsData)) {
      api.asset.fetchAsset(assetId).then(
        (assetInfo) => {
          if (requestToken !== assetRequestToken.current) return;
          dispatch(addNewAsset({ asset: assetInfo }));
          dispatch(addOpenedAsset({ assetId }));
          setAssetInfo(assetInfo);
        },
        () => {
          if (requestToken !== assetRequestToken.current) return;
          navigate('/');
          if (assetId) dispatch(closeOpenedAsset({ assetId }));
        },
      );
    } else {
      if (assetId && queriedAssetsData) setAssetInfo(findOpenedAsset(queriedAssetsData, assetId));
    }
  }, [assetId]);

  // Keep track of the search URL params for the asset view,
  // so that we can restore it when the user navigates back to this asset
  useEffect(() => {
    if (assetId) {
      dispatch(setAssetLastSearchQuery({ assetId, query: location.search }));
    }
  }, [assetId, location.search, dispatch]);

  useEffect(() => {
    if (assetId && queriedAssetsData) setAssetInfo(findOpenedAsset(queriedAssetsData, assetId));
  }, [queriedAssetsData]);

  useEffect(() => {
    if (!assetInfo) return;

    const assetViewObj = assetViews?.[assetInfo!.type];
    if (!assetViewObj) {
      showErrorMessage(
        `No active asset view template found for asset type ${assetInfo.type}. Please contact your administrator.`,
      );
      setAssetViewTemplate(null);
      return;
    }
    // Add a check if we have a dashboard yaml defined on asset property level
    if (assetInfo.assetDashboardTemplate) {
      dispatch(addTemplate({ template: assetInfo.assetDashboardTemplate }));
    }
    if (assetInfo.assetEntityTemplate) {
      dispatch(addTemplate({ template: assetInfo.assetEntityTemplate }));
    }
    setAssetViewTemplate(assetViewObj);
  }, [assetViews, assetInfo]);

  const sectionIndexContext = getSectionsIndexContext(`_asset_${assetId}`);

  return assetId && assetInfo && assetViewTemplate ? (
    <SectionsIndexWrapper sectionIndexContext={sectionIndexContext}>
      <div className={styles.wrapper} key={assetId}>
        <AssetViewFactory
          key={assetId}
          activeAssetInfoId={assetId}
          assetInfo={assetInfo!}
          setAssetInfo={setAssetInfo}
          assetViewTemplate={assetViewTemplate}
          sectionIndexContext={sectionIndexContext}
          assetOntology={ontology.assetTypes[assetInfo.type]}
        />
      </div>
    </SectionsIndexWrapper>
  ) : (
    <div className={styles.spinnerWrapper}>
      <FontAwesomeIcon icon={['fas', 'spinner']} spin className={styles.spinner} />
    </div>
  );
}
